import { useParams, Link } from "react-router-dom";
import { Phone, MapPin, ArrowRight, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import ServiceCard from "@/components/ServiceCard";
import ContactForm from "@/components/ContactForm";
import { renderSection } from "@/components/ServiceSections";
import { cityContent } from "@/data/city-content";
import {
  PHONE,
  PHONE_LINK,
  BUSINESS_NAME,
  CITY_PAGES,
  PLUMBING_SERVICES,
  PURIFICATION_SERVICES,
  SOCIAL_PROFILES,
  KNOWS_ABOUT,
} from "@/lib/constants";

const CityPage = () => {
  const { citySlug } = useParams<{ citySlug: string }>();
  const city = CITY_PAGES.find((c) => c.slug === citySlug);
  const content = citySlug ? cityContent[citySlug] : null;

  if (!city) {
    return (
      <div className="container py-24 text-center">
        <h1 className="text-3xl font-bold mb-4">City Not Found</h1>
        <Link to="/service-areas/">
          <Button variant="outline">View All Service Areas</Button>
        </Link>
      </div>
    );
  }

  const cityName = `${city.name}, ${city.state}`;
  const baseUrl = "https://truamericanplumbing.com";

  const citySchema = {
    "@context": "https://schema.org",
    "@type": "Plumber",
    "@id": `${baseUrl}/#organization`,
    name: BUSINESS_NAME,
    alternateName: ["Tru American Plumbing & Purification", "Tru American Plumbing"],
    legalName: "Tru American Plumbing Purifications LLC",
    founder: { "@type": "Person", name: "Thomas F Trujillo" },
    telephone: "+14803222567",
    url: baseUrl,
    areaServed: { "@type": "City", name: city.name, addressRegion: city.state },
    geo: { "@type": "GeoCoordinates", latitude: "33.3542164", longitude: "-112.4815896" },
    openingHoursSpecification: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      opens: "07:00",
      closes: "18:00",
    },
    sameAs: SOCIAL_PROFILES,
    knowsAbout: KNOWS_ABOUT,
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: baseUrl },
      { "@type": "ListItem", position: 2, name: "Service Areas", item: `${baseUrl}/service-areas` },
      { "@type": "ListItem", position: 3, name: `${city.name}, ${city.state}`, item: `${baseUrl}/service-areas/${city.slug}` },
    ],
  };

  return (
    <>
      <SEO
        title={
          content?.seoTitle ??
          `Plumber in ${cityName} - Plumbing & Water Purification`
        }
        description={
          content?.seoDescription ??
          (content
            ? `${content.headline} ${content.headlineAccent}. ${BUSINESS_NAME} provides plumbing and water purification in ${cityName}. Call (480) 322-2567.`
            : `Expert plumber in ${cityName}. ${BUSINESS_NAME} provides drain cleaning, water heater repair, leak detection, and water softener installation. Call (480) 322-2567.`)
        }
        canonicalUrl={`/service-areas/${city.slug}`}
        schema={[citySchema, breadcrumbSchema]}
      />

      {/* Breadcrumb */}
      <div className="bg-muted/30 border-b border-border">
        <div className="container py-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Link to="/" className="hover:text-primary">Home</Link>
            <span>/</span>
            <Link to="/service-areas/" className="hover:text-primary">Service Areas</Link>
            <span>/</span>
            <span className="text-foreground">{city.name}, {city.state}</span>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-muted/50 to-background">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
              <MapPin className="h-4 w-4" />
              Serving {cityName}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              {content ? (
                <>
                  {content.headline}{" "}
                  <span className="text-gradient-copper">{content.headlineAccent}</span>
                </>
              ) : (
                <>
                  Trusted <span className="text-gradient-copper">Plumber in {city.name}</span>, Arizona
                </>
              )}
            </h1>
            <p className="text-xl text-muted-foreground mb-8">
              {content
                ? content.introParagraph
                : `${BUSINESS_NAME} provides professional plumbing and water purification services to homeowners and businesses in ${cityName}. Licensed, bonded, and insured for your peace of mind.`}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={PHONE_LINK}>
                <Button variant="hero" size="xl" className="w-full sm:w-auto gap-2">
                  <Phone className="h-5 w-5" />
                  Call {PHONE}
                </Button>
              </a>
              <Link to="/contact/">
                <Button variant="heroOutline" size="xl" className="w-full sm:w-auto gap-2">
                  Request Service
                  <ArrowRight className="h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Unique section (if positioned before services — Tolleson, Avondale) */}
      {content?.uniqueSection &&
        (citySlug === "tolleson-az" || citySlug === "avondale-az") &&
        renderSection(content.uniqueSection, 0)}

      {/* Local Challenges */}
      {content && (
        <section className="py-16 md:py-24 bg-muted/20">
          <div className="container">
            <h2 className="text-3xl font-bold text-center mb-8">
              <span className="text-gradient-copper">{content.localChallenges.heading}</span>
            </h2>
            <div className="grid sm:grid-cols-2 gap-4 max-w-4xl mx-auto">
              {content.localChallenges.items.map((item, i) => (
                <div
                  key={i}
                  className="flex items-start gap-3 bg-card border border-border rounded-lg p-4"
                >
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <p className="text-foreground/80 text-sm">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Unique section (middle position — Goodyear, Glendale, Surprise) */}
      {content?.uniqueSection &&
        (citySlug === "goodyear-az" || citySlug === "glendale-az" || citySlug === "surprise-az") &&
        renderSection(content.uniqueSection, 1)}

      {/* Services */}
      <section className="py-16 md:py-24">
        <div className="container">
          <h2 className="text-3xl font-bold text-center mb-4">
            Plumbing Services in <span className="text-gradient-copper">{city.name}</span>
          </h2>
          <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
            From emergency repairs to water heater installations, we handle all
            plumbing needs for {cityName} residents.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {PLUMBING_SERVICES.slice(0, 6).map((service) => (
              <ServiceCard key={service.slug} {...service} />
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/services/">
              <Button variant="outline" className="gap-2">
                View All Services
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Water Purification */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container">
          <h2 className="text-3xl font-bold text-center mb-4">
            Water Purification in <span className="text-gradient-copper">{city.name}</span>
          </h2>
          <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
            {city.name} homes face Arizona's notoriously hard water. Protect your
            pipes, appliances, and family with our water treatment solutions.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto">
            {PURIFICATION_SERVICES.map((service) => (
              <ServiceCard
                key={service.slug}
                {...service}
                basePath="/water-purification"
              />
            ))}
          </div>
        </div>
      </section>

      {/* Unique section (after services — Peoria, Phoenix, Buckeye) */}
      {content?.uniqueSection &&
        (citySlug === "peoria-az" || citySlug === "phoenix-az" || citySlug === "buckeye-az") &&
        renderSection(content.uniqueSection, 2)}

      {/* Why Choose Us */}
      <section className="py-16 md:py-24">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl font-bold mb-6">
                Why {city.name} Chooses{" "}
                <span className="text-gradient-copper">Tru American</span>
              </h2>
              <div className="space-y-4">
                {(content?.whyChooseUs || [
                  "Local company based in Tolleson – we know the West Valley",
                  `Fast response times for ${city.name} service calls`,
                  "Upfront pricing with no hidden fees",
                  "Licensed, bonded, and insured for your protection",
                  "Residential and light commercial plumbing expertise",
                ]).map((reason, i) => (
                  <div key={i} className="flex gap-3">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <p className="text-foreground/80">{reason}</p>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-6">
                Get Service in {city.name}
              </h3>
              <ContactForm />
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default CityPage;
